/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { normalizeLocale, resolveLocale } from '../../shared/i18n';
import type { Locale } from '../../shared/i18n';
import { en, ru } from './catalog';
import type { TranslationKey } from './catalog';
import { telegram } from '../telegram/bridge';

const STORAGE_KEY = 'walld.preferredLocale';
type Translate = (key: TranslationKey, values?: Record<string, string | number>) => string;
interface I18nValue { locale: Locale; setLocale: (locale: Locale) => void; t: Translate }

function interpolate(source: string, values: Record<string, string | number>): string {
  let result = source;
  for (const [name, value] of Object.entries(values)) result = result.replaceAll(`{${name}}`, String(value));
  return result;
}

const englishT: Translate = (key, values = {}) => interpolate(en[key], values);
const I18nContext = createContext<I18nValue>({ locale: 'en', setLocale: () => undefined, t: englishT });

export function localLocalePreference(): Locale | null {
  try { return normalizeLocale(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => telegram.isAvailable()
    ? resolveLocale({ telegramLanguageCode: telegram.languageCode() })
    : resolveLocale({ preferredLocale: localLocalePreference() }));
  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* Private browsing may disable storage. */ }
  }, []);
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);
  const t = useCallback<Translate>((key, values = {}) => {
    return interpolate((locale === 'ru' ? ru : en)[key], values);
  }, [locale]);
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  return useContext(I18nContext);
}
